import React, { useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import PaginationComp from "./dashboard-components/paginate";

interface AccountData {
  userId: string;
  refereeCode: string;
  FullName: string;
  AccountNumber: string;
  AccountName: string;
  Bank: string;
  ngnBalance: string;
  ngnCommissionBalance: string;
}

interface AccountsTableProps {
  data?: {
    data: AccountData[];
    page: number;
    pageSize: number;
    total: string;
    totalPages: number;
  };
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
}

const formatCurrency = (amount: string, decimals: number = 2) => {
  return Number(amount || "0").toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const AccountsTable = ({
  data,
  isLoading,
  page,
  onPageChange,
}: AccountsTableProps) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (userId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return <div className="flex justify-center p-4">Loading...</div>;
  }

  const columns = [
    { key: "refId", label: "REFERRAL ID", align: "left" },
    { key: "full_name", label: "FULL NAME", align: "left" },
    { key: "email", label: "EMAIL ADDRESS", align: "left" },
    { key: "bank", label: "NGN BANK", align: "left" },
    { key: "AccountNo", label: "ACCOUNT NO", align: "left" },
    { key: "actions", label: "ACTIONS", align: "center" },
  ];

  return (
    <div className="bg-white rounded-lg w-full">
      <div className="overflow-x-auto">
        <table className="appearance-none bg-white min-w-full  mb-6 ">
          <thead className="bg-[#fff] appearance-none">
            <tr className="border-b bg-gray-50">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`font-normal text-sm text-primary py-4 whitespace-nowrap px-4 border-r border-slate-50 ${
                    column.align === "center"
                      ? "text-center"
                      : `text-${column.align}`
                  }`}
                >
                  <span className="text-[#5C6F7F] font-bold text-[12px] leading-[18px]">
                    {column.label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="mt-3 pt-3 w-full space-y-8 border-r border-slate-50">
            {data?.data.map((account: AccountData) => (
              <React.Fragment key={account.userId}>
                <tr className="appearance-none my-4 border border-slate-50 even:bg-white odd:bg-slate-100">
                  <td className="py-4 px-4 text-sm text-[#0eb683] underline cursor-pointer">
                    <div className="truncate" title={account.refereeCode}>
                      {account.refereeCode || "N/A"}
                    </div>
                  </td>
                  <td className="py-4 px-4 text-sm text-[#202223]">
                    {account.FullName || "N/A"}
                  </td>
                  <td className="py-4 px-4 text-sm text-[#202223]">{"N/A"}</td>
                  <td className="py-4 px-4 text-sm text-[#202223]">
                    {account.Bank || "N/A"}
                  </td>
                  <td className="py-4 px-4 text-sm text-[#202223]">
                    {account.AccountNumber || "N/A"}
                  </td>
                  <td className="py-4 px-4 text-center">
                    <button
                      onClick={() => toggleRow(account.userId)}
                      className="inline-flex items-center px-3 py-1 text-sm  hover:bg-gray-100 rounded-md transition-colors border border-[#0eb683] text-[#0eb683]"
                    >
                      View More
                      {expandedRows.has(account.userId) ? (
                        <ChevronUp className="h-4 w-4 ml-1" />
                      ) : (
                        <ChevronDown className="h-4 w-4 ml-1" />
                      )}
                    </button>
                  </td>
                </tr>
                {expandedRows.has(account.userId) && (
                  <tr className="bg-gray-50">
                    <td colSpan={6} className="py-4 px-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">
                            Account Details
                          </h4>
                          <dl className="space-y-2">
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">
                                NGN Balance:
                              </dt>
                              <dd className="text-sm text-gray-900">
                                ₦ {formatCurrency(account.ngnBalance)}
                              </dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">
                                NGN Commission Balance:
                              </dt>
                              <dd className="text-sm text-gray-900">
                                ₦ {formatCurrency(account.ngnCommissionBalance)}
                              </dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-sm text-gray-500">
                                Account Name:
                              </dt>
                              <dd className="text-sm text-gray-900">
                                {account.AccountName || "N/A"}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {data && (
        <div className="px-4">
          <PaginationComp
            currentPage={page}
            totalPages={data.totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default AccountsTable;
